import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API, apiErrorMessage, formatINR } from "@/lib/api";
import { CONSENT } from "@/constants/testIds";
import { toast } from "sonner";
import { CheckCircle, XCircle, ShieldCheck } from "@phosphor-icons/react";
import BrandLogo from "@/components/BrandLogo";
import { motion } from "framer-motion";

export default function ConsentPage() {
  const { token } = useParams();
  const [loan, setLoan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [signature, setSignature] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/public/loans/${token}`);
        setLoan(data);
        setSignature(data.borrower_name || "");
      } catch (err) {
        toast.error(apiErrorMessage(err));
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const submit = async (decision) => {
    if (!signature.trim()) {
      toast.error("Please type your full name as signature");
      return;
    }
    setSubmitting(true);
    // Best-effort location capture — don't block if the user denies it.
    const gps = await new Promise((resolve) => {
      if (!("geolocation" in navigator)) return resolve(null);
      const timer = setTimeout(() => resolve(null), 3500);
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          clearTimeout(timer);
          resolve({
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
            accuracy: pos.coords.accuracy,
          });
        },
        () => {
          clearTimeout(timer);
          resolve(null);
        },
        { timeout: 3000, enableHighAccuracy: false },
      );
    });
    try {
      const { data } = await axios.post(
        `${API}/public/loans/${token}/consent`,
        { decision, borrower_signature_name: signature.trim(), gps }
      );
      setLoan({
        ...loan,
        consent_status: data.consent_status,
        borrower_signature_name: signature.trim(),
        consent_at: new Date().toISOString(),
        borrower_identity_attached: !!data.identity_attached,
      });
      toast.success(
        decision === "agreed"
          ? (data.identity_attached
              ? "Consent recorded — your verified identity is attached"
              : "Thank you — consent recorded")
          : "You've declined this loan agreement"
      );
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center text-neutral-500 text-sm">
        Loading agreement…
      </div>
    );
  }

  if (!loan) {
    return (
      <div className="min-h-screen grid place-items-center px-6">
        <div className="lele-card p-8 max-w-md text-center">
          <XCircle size={40} weight="duotone" className="mx-auto text-rose-400 mb-3" />
          <div className="font-heading text-2xl mb-2">Link not found</div>
          <p className="text-neutral-500 text-sm">
            This consent link may have expired or is incorrect.
          </p>
        </div>
      </div>
    );
  }

  const decided = loan.consent_status !== "awaiting";

  return (
    <div
      data-testid={CONSENT.root}
      className="min-h-screen relative"
      style={{
        backgroundImage:
          "url('https://images.unsplash.com/photo-1578662996442-48f60103fc96?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGRhcmslMjBwcmVtaXVtJTIwdGV4dHVyZSUyMGJhY2tncm91bmR8ZW58MHx8fHwxNzgzNzY0NjYwfDA&ixlib=rb-4.1.0&q=85')",
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      <div className="relative z-10 max-w-2xl mx-auto px-5 md:px-8 py-10 md:py-16">
        <div className="flex items-center gap-2 mb-8">
          <BrandLogo size={38} showWordmark />
        </div>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="lele-card p-6 md:p-10 bg-black/60 backdrop-blur-xl"
        >
          <div className="text-[11px] uppercase tracking-[0.3em] text-neutral-500 mb-3 flex items-center gap-2">
            <ShieldCheck size={13} weight="fill" className="text-indigo-400" />
            Loan agreement — informal hand loan
          </div>
          <h1 className="font-heading text-3xl md:text-4xl tracking-tight mb-6">
            {loan.lender_name} has documented a loan with you.
          </h1>

          <div className="grid grid-cols-2 gap-4 mb-6">
            <Field label="Lender" value={loan.lender_name} />
            <Field label="Borrower" value={loan.borrower_name} />
            <Field label="Principal" value={formatINR(loan.amount)} highlight />
            <Field
              label="Repayable"
              value={formatINR(loan.total_repayable)}
              highlight
            />
            <Field label="Interest" value={`${loan.interest_rate}% / yr`} />
            <Field label="Tenure" value={`${loan.tenure_days} days`} />
            <Field
              label="Due date"
              value={new Date(loan.due_date).toLocaleDateString("en-IN", {
                day: "2-digit",
                month: "long",
                year: "numeric",
              })}
            />
            {loan.purpose ? (
              <Field label="Purpose" value={loan.purpose} />
            ) : (
              <Field label="Purpose" value="—" />
            )}
          </div>

          <div className="rounded-xl border border-white/10 p-4 text-sm text-neutral-400 leading-relaxed bg-white/[0.02]">
            By tapping <span className="text-neutral-200">I agree</span>, you
            acknowledge that you have received{" "}
            <span className="text-neutral-200">{formatINR(loan.amount)}</span>{" "}
            from <span className="text-neutral-200">{loan.lender_name}</span>{" "}
            and undertake to repay{" "}
            <span className="text-neutral-200">
              {formatINR(loan.total_repayable)}
            </span>{" "}
            by{" "}
            <span className="text-neutral-200">
              {new Date(loan.due_date).toLocaleDateString("en-IN")}
            </span>
            . This record is timestamped as your digital consent.
          </div>

          {loan.borrower_identity_preview && (
            <div
              data-testid="consent-identity-badge"
              className="mt-4 rounded-xl border border-indigo-400/30 bg-indigo-500/[0.06] p-4"
            >
              <div className="flex items-start gap-3">
                <div className="h-9 w-9 rounded-lg bg-indigo-500/15 border border-indigo-400/40 grid place-items-center shrink-0">
                  <ShieldCheck size={18} weight="fill" className="text-indigo-300" />
                </div>
                <div className="flex-1 text-sm">
                  <div className="text-neutral-100 font-medium">
                    Signing as verified{" "}
                    <span className="text-indigo-300">
                      {loan.borrower_identity_preview.verified_name}
                    </span>
                  </div>
                  <div className="text-[11px] text-neutral-500 mt-0.5">
                    {loan.borrower_identity_preview.id_type?.toUpperCase()} ·{" "}
                    <span className="font-mono">
                      {loan.borrower_identity_preview.id_number_masked || "—"}
                    </span>{" "}
                    · {loan.borrower_identity_preview.lele_id}
                  </div>
                  <div className="text-[11px] text-neutral-500 mt-1">
                    Your verified identity block will be attached to this
                    agreement's PDF for stronger legal proof.
                  </div>
                </div>
              </div>
            </div>
          )}

          {decided ? (
            <div
              className={`mt-6 rounded-xl p-5 border ${
                loan.consent_status === "agreed"
                  ? "border-emerald-500/30 bg-emerald-500/5"
                  : "border-rose-500/30 bg-rose-500/5"
              }`}
            >
              <div className="flex items-center gap-2 mb-1">
                {loan.consent_status === "agreed" ? (
                  <CheckCircle size={18} weight="fill" className="text-emerald-400" />
                ) : (
                  <XCircle size={18} weight="fill" className="text-rose-400" />
                )}
                <span className="font-medium">
                  {loan.consent_status === "agreed"
                    ? "You've agreed to this loan"
                    : "You've declined this loan"}
                </span>
              </div>
              <div className="text-xs text-neutral-500">
                Signed as{" "}
                <span className="text-neutral-300">
                  {loan.borrower_signature_name}
                </span>{" "}
                on{" "}
                {loan.consent_at
                  ? new Date(loan.consent_at).toLocaleString("en-IN")
                  : "—"}
              </div>
            </div>
          ) : (
            <>
              <label className="block text-[11px] uppercase tracking-[0.2em] text-neutral-500 mt-6 mb-2">
                Type your full name (digital signature)
              </label>
              <input
                data-testid={CONSENT.signatureInput}
                type="text"
                className="lele-input w-full rounded-lg px-4 py-3"
                value={signature}
                onChange={(e) => setSignature(e.target.value)}
                placeholder="Your full name"
              />

              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <button
                  data-testid={CONSENT.declineBtn}
                  onClick={() => submit("declined")}
                  disabled={submitting}
                  className="lele-btn-ghost flex items-center justify-center gap-2"
                >
                  <XCircle size={16} weight="fill" /> Decline
                </button>
                <button
                  data-testid={CONSENT.agreeBtn}
                  onClick={() => submit("agreed")}
                  disabled={submitting}
                  className="lele-btn-primary flex items-center justify-center gap-2"
                >
                  <CheckCircle size={16} weight="fill" />
                  {submitting ? "Recording…" : "I agree"}
                </button>
              </div>
            </>
          )}
        </motion.div>

        <div className="mt-6 text-center text-[11px] uppercase tracking-[0.3em] text-neutral-600">
          LeLe · Protect Every Rupee · Preserve Every Relationship
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, highlight }) {
  return (
    <div className="rounded-xl border border-white/5 bg-white/[0.02] p-3">
      <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 mb-1">
        {label}
      </div>
      <div
        className={
          highlight
            ? "font-heading text-lg text-indigo-300"
            : "text-sm text-neutral-200"
        }
      >
        {value}
      </div>
    </div>
  );
}
